import type { ReactNode } from "react";

import type { ComponentFlowPayload, ComponentGroup } from "@busright/data-contract";
import { COLORS } from "@busright/ui";

import { formatInt, formatPct } from "@/lib/format";

// R1/R2 — field inventory + active-device context, framed as CURRENT-STATE (not a month-end trend —
// esper_fleet_roster.snapshot_ts only spans ~36 days, so there is no true month-end series yet).
// Tablets are a REAL Esper roster count; cradle/harness/SIM have no per-unit field census and are
// shown as cumulative order-flow totals (parsed from E2 `description`) with their own confidence.

const COMPONENT_LABEL: Record<ComponentGroup, string> = {
  tablet: "Tablet",
  cradle: "Cradle / mount",
  harness: "Charging harness",
  sim: "SIM",
};

// Mean confidence across a component's parsed rows — surfaced per component so "cradle: 8,152" reads
// with the same honesty as the tablet figure, which needs none (Esper roster = ground truth).
const meanConfidence = (
  rows: ComponentFlowPayload["rows"],
  component: ComponentGroup,
): number | null => {
  const matching = rows.filter((r) => r.component === component);
  if (matching.length === 0) {
    return null;
  }
  return matching.reduce((sum, r) => sum + r.confidence, 0) / matching.length;
};

const PCT_SCALE = 100;

// Confidence cell text for one component row: tablets are real (no parse confidence needed);
// cradle/harness/SIM show the mean parse confidence, or an em dash if no rows matched.
const confidenceCell = (isTablet: boolean, confidence: number | null): string => {
  if (isTablet) {
    return "n/a (real)";
  }
  return confidence === null ? "—" : formatPct(confidence * PCT_SCALE, 0);
};

export interface ScFieldInventoryProps {
  readonly componentFlow: ComponentFlowPayload;
  readonly tabletsInField: number;
  readonly activeDevices: number;
  readonly totalDevices: number;
}

export const ScFieldInventory = ({
  componentFlow,
  tabletsInField,
  activeDevices,
  totalDevices,
}: ScFieldInventoryProps): ReactNode => {
  const activePct = totalDevices > 0 ? (activeDevices / totalDevices) * PCT_SCALE : 0;

  return (
    <div>
      <div className="metric-strip" style={{ marginBottom: 14 }}>
        <div className="metric-chip">
          <span className="mc-label">Tablets in field (real, Esper)</span>
          <span className="mc-value br-tabular-nums">{formatInt(tabletsInField)}</span>
        </div>
        <div className="metric-chip">
          <span className="mc-label">Active devices (last ~30d)</span>
          <span className="mc-value br-tabular-nums">
            {formatInt(activeDevices)} / {formatInt(totalDevices)}
          </span>
        </div>
        <div className="metric-chip">
          <span className="mc-label">Active share</span>
          <span className="mc-value br-tabular-nums">{formatPct(activePct)}</span>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table className="data-table" style={{ minWidth: 480 }}>
          <thead>
            <tr>
              <th>Component</th>
              <th className="num">Cumulative shipped (order-flow)</th>
              <th className="num">Source</th>
              <th className="num">Confidence</th>
            </tr>
          </thead>
          <tbody>
            {(["tablet", "cradle", "harness", "sim"] as const).map((component) => {
              const confidence = meanConfidence(componentFlow.rows, component);
              const isTablet = component === "tablet";
              return (
                <tr key={component}>
                  <td>{COMPONENT_LABEL[component]}</td>
                  <td className="num br-tabular-nums">
                    {formatInt(componentFlow.totalsByComponent[component])}
                  </td>
                  <td className="num" style={{ color: COLORS.textMuted, fontSize: 11 }}>
                    {isTablet ? "Esper + order-flow" : "order-flow estimate"}
                  </td>
                  <td className="num br-tabular-nums" style={{ color: COLORS.textMuted }}>
                    {confidenceCell(isTablet, confidence)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 10, fontSize: 11, color: "var(--br-text-muted)" }}>
        Current-state snapshot, not a month-end trend — the Esper roster only carries ~36 days of
        history. Cradle / harness / SIM have no per-unit field roster (Esper tracks tablets only);
        their counts are cumulative order-flow, not a census.
      </p>
    </div>
  );
};
